/**
 * explainRefreshError — turn a decision-analysis failure payload into
 * something a site owner can act on.
 *
 * Live prod 2026-08-03: the Findings matrix printed `refreshError`
 * verbatim. For a schema rejection that is a raw Zod issue array —
 *
 *   [{"code":"custom","path":["sections",3,"blocks"],"message":"A required
 *   decision-analysis section cannot be empty."},{"code":"custom",...
 *
 * — rendered with `break-all` and clamped to three lines, so the user saw
 * mangled JSON and learned nothing. Worse, the two failure families need
 * opposite responses: `input-stale` clears on retry, while an empty-section
 * rejection is a generation fault the user cannot influence at all. Showing
 * both as undifferentiated red text invites pointless retry loops.
 *
 * Pure module — no React, no network. The renderer owns presentation.
 */

import { describeOptionalEvidenceSections } from './autoRebindGate';

/** What the user should do about it. Drives copy, not styling. */
export type RefreshErrorRemedy =
  /** Transient/binding — a retry genuinely resolves it. */
  | 'retry'
  /** The model returned unusable output. Retry may work by luck; nothing
   *  the user supplies changes it. Never tell them to upload documents. */
  | 'regenerate'
  /** Real missing input — the user can fix this by supplying evidence. */
  | 'supply-evidence'
  /**
   * The report states something the evidence contradicts, and every such
   * finding is an ANSWERABLE question. Open the report and settle them; the
   * answers persist as evidence, so the rerun generates from corrected inputs.
   */
  | 'resolve-truth-questions'
  /**
   * The platform stopped re-running this analysis by itself (the
   * consecutive-rebind cap). One operator-started rerun is the reset; if THAT
   * run also lands stale, the currency check is at fault and more runs cannot
   * help.
   */
  | 'manual-rerun'
  /** Not recognised; show the raw text rather than inventing a diagnosis. */
  | 'unknown';

export interface ExplainedRefreshError {
  /** One sentence, plain language. Safe to render on its own. */
  headline: string;
  /** Optional second line with specifics (counts, section indices). */
  detail?: string;
  remedy: RefreshErrorRemedy;
  /** True when the user can meaningfully act. False ⇒ don't imply they can. */
  userActionable: boolean;
  /** The original payload, always preserved for tooltips/support. */
  raw: string;
}

interface ZodIssueLike {
  code?: unknown;
  path?: unknown;
  message?: unknown;
}

function parseIssues(raw: string): ZodIssueLike[] | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[')) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) return null;
    const issues = parsed.filter(
      (i): i is ZodIssueLike => typeof i === 'object' && i !== null,
    );
    return issues.length > 0 ? issues : null;
  } catch {
    return null;
  }
}

function messageOf(issue: ZodIssueLike): string {
  return typeof issue.message === 'string' ? issue.message : '';
}

/** `["sections", 3, "blocks"]` → 3. Null when the path isn't a section path. */
function sectionIndexOf(issue: ZodIssueLike): number | null {
  if (!Array.isArray(issue.path)) return null;
  const [head, index] = issue.path;
  if (head !== 'sections' || typeof index !== 'number') return null;
  return index;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

const EMPTY_SECTION = 'A required decision-analysis section cannot be empty.';
const NO_CONDITIONS =
  'A conditional decision must reference at least one explicit condition.';

/**
 * Classify a raw `refreshError` string.
 *
 * Recognises the failure families seen in production (input-stale binding
 * races, deterministic hard-gate quality failures) plus the schema rejections
 * that carry Zod issue arrays. Anything unrecognised is passed through
 * untouched with `remedy: 'unknown'` — guessing at a diagnosis is worse
 * than showing the engine's own words.
 */
export function explainRefreshError(
  raw: string | null | undefined,
  /**
   * How many answerable truth questions the held analysis carries. Optional
   * and defaulting to 0 so every existing caller keeps its current copy —
   * only a caller that KNOWS there are questions gets the resolution advice.
   */
  truthQuestionCount = 0,
  /**
   * Pointer facts the error STRING cannot carry. `autoRebindExhausted` is
   * `Boolean(pointer.autoRebindExhaustedAt)`; `autoRebindStreak` is the
   * consecutive input-stale commit count, used only to make the copy concrete.
   * Defaulted so every existing caller keeps its current behaviour.
   */
  context: {
    autoRebindExhausted?: boolean;
    autoRebindStreak?: number;
    /**
     * `pointer.autoRebindSkippedSections`: the stale commit armed no automatic
     * rerun because only these optional currency sections moved
     * (autoRebindGate). Newer evidence is available; nothing is broken.
     */
    optionalEvidenceSections?: readonly string[];
  } = {},
): ExplainedRefreshError | null {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (text.length === 0) return null;

  // --- Family 1: the analysis committed against inputs that moved underneath it.
  if (text.includes('decision-analysis-input-stale')) {
    /*
     * OPTIONAL EVIDENCE ONLY (owner decision 4, 2026-09-30): newer public
     * sentiment / economic evidence arrived. That never re-runs an analysis
     * automatically, so say so instead of promising a refresh.
     */
    if (context.optionalEvidenceSections && context.optionalEvidenceSections.length > 0) {
      return {
        headline: `Newer optional evidence is available (${describeOptionalEvidenceSections(context.optionalEvidenceSections)}).`,
        detail:
          'It arrived while this analysis was running. Optional evidence does not re-run analyses automatically — re-run the report when you want it included.',
        remedy: 'manual-rerun',
        userActionable: true,
        raw: text,
      };
    }
    /*
     * THE CAP MUST NOT BE SILENT. An input-stale commit arms one automatic
     * rebind; past AUTO_REBIND_MAX_CONSECUTIVE finalizeReportRevision stops
     * arming them and stamps `autoRebindExhaustedAt`. Without this branch the
     * row keeps promising "the new run will bind to the current evidence"
     * while nothing is running and nothing ever will — the same sentence, now
     * false.
     */
    if (context.autoRebindExhausted) {
      const runs = typeof context.autoRebindStreak === 'number'
        && Number.isFinite(context.autoRebindStreak)
        && context.autoRebindStreak > 1
        ? Math.floor(context.autoRebindStreak)
        : null;
      return {
        headline: runs
          ? `Automatic refresh gave up after ${runs} stale runs — nothing will re-run this analysis on its own.`
          : 'Automatic refresh gave up — nothing will re-run this analysis on its own.',
        detail:
          'Open the report and re-run it to try once more. If that run is stale too, the currency check is at fault — re-running will not clear it.',
        remedy: 'manual-rerun',
        userActionable: true,
        raw: text,
      };
    }
    return {
      headline:
        'The site’s evidence changed while this analysis was running, so the result was discarded.',
      detail: 'Re-run the report — the new run will bind to the current evidence.',
      remedy: 'retry',
      userActionable: true,
      raw: text,
    };
  }

  // --- Family 2: the run committed, but the produced analysis failed the
  // deterministic quality hard gates (this arrives via refreshReason once the
  // worker has cleared any crash-era refreshError). Generation fault — the
  // site's evidence is fine and nothing the user supplies changes it.
  if (text.includes('deterministic semantic-analysis hard gates')) {
    /*
     * TRUTH QUESTIONS CHANGE THE ADVICE (2026-08-06).
     *
     * This branch used to say "retrying may produce a passing analysis" with
     * `userActionable: false`. That was true when a blocking finding was a dead
     * end. It no longer is: every truth violation now yields an answerable
     * question, so RETRYING IS THE WRONG ADVICE — it regenerates against
     * unchanged evidence and reproduces the same finding, which is precisely
     * the loop the resolution flow removed.
     *
     * The count comes from the caller because this function only ever sees the
     * error string. Absent (0) it keeps the old copy, so a report genuinely
     * without questions is unaffected.
     */
    if (truthQuestionCount > 0) {
      const n = truthQuestionCount;
      return {
        headline: `${n} statement${n === 1 ? '' : 's'} in this analysis disagree${n === 1 ? 's' : ''} with the site evidence.`,
        detail: 'Open the analysis and confirm what is correct — your answers are saved to the evidence record, then re-run.',
        remedy: 'resolve-truth-questions',
        userActionable: true,
        raw: text,
      };
    }
    return {
      headline:
        'The analysis was generated but failed deterministic quality checks, so it cannot support a decision.',
      detail:
        'This is a generation fault, not missing site data — retrying may produce a passing analysis.',
      remedy: 'regenerate',
      userActionable: false,
      raw: text,
    };
  }

  // --- Family 3: schema rejection carrying a Zod issue array.
  const issues = parseIssues(text);
  if (issues) {
    const emptySections = issues.filter(i => messageOf(i) === EMPTY_SECTION);
    const missingConditions = issues.filter(i => messageOf(i) === NO_CONDITIONS);

    if (emptySections.length > 0 && emptySections.length === issues.length) {
      const n = emptySections.length;
      const indices = emptySections
        .map(sectionIndexOf)
        .filter((i): i is number => i != null)
        .sort((a, b) => a - b);
      return {
        headline: `The analysis came back with ${n} required ${plural(n, 'section', 'sections')} empty, so it could not be published.`,
        detail:
          indices.length > 0
            ? `Empty ${plural(indices.length, 'section', 'sections')}: ${indices.join(', ')}. This is a generation fault, not missing site data — retrying may produce a complete analysis.`
            : 'This is a generation fault, not missing site data — retrying may produce a complete analysis.',
        remedy: 'regenerate',
        userActionable: false,
        raw: text,
      };
    }

    if (missingConditions.length > 0 && missingConditions.length === issues.length) {
      return {
        headline:
          'The analysis reached a conditional verdict but did not list the conditions it depends on.',
        detail:
          'This is a generation fault, not missing site data — retrying may produce a complete analysis.',
        remedy: 'regenerate',
        userActionable: false,
        raw: text,
      };
    }

    // Mixed or unrecognised issue set — summarise honestly without pretending
    // to know which half matters.
    const distinct = [...new Set(issues.map(messageOf).filter(Boolean))];
    return {
      headline: `The analysis failed ${issues.length} content ${plural(issues.length, 'check', 'checks')} and could not be published.`,
      detail: distinct.slice(0, 3).join(' '),
      remedy: 'regenerate',
      userActionable: false,
      raw: text,
    };
  }

  // --- Unrecognised: surface the engine's own words.
  return {
    headline: text,
    remedy: 'unknown',
    userActionable: true,
    raw: text,
  };
}
